import React, { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { TabPaneElement } from "../../../ValidatableFormTabs/ValidatableFormTabs"
import { Formik } from "formik"
import Tab from "react-bootstrap/Tab"
import { AgentType, RoutingAgentValues } from "../../../../models/agent"
import { getDefaultRoutingAgentValues, getValuesFromRoutingAgent } from "../../../../utility/agents/agentValues"
import { useDispatch, useSelector } from "react-redux"
import { Dispatch } from "../../../../utility/common/storeHelper"
import { AgentFormProps } from "../../AgentForm"
import FormikRoutingAgentFormGeneral from "./RoutingAgentFormGeneral"
import FormikRoutingAgentFormAdvanced from "./RoutingAgentAdvanced"
import { buildRoutingAgentRequest } from "../../../../utility/agents/agentRequest"
import { createAgent, getAgentDeclarations, updateAgent } from "../../../../store/agents/thunks"
import { selectCurrentProject } from "../../../../store/projects/selectors"
import OnSubmitValidationError from "../../../OnSubmitValidationError/OnSubmitValidationError"
import { selectAgentDeclarations } from "../../../../store/agents/selectors"
import AgentFormLoader from "../../../AgentForm/AgentFormLoader"
import { getValidationSchema } from "../../../../utility/agents/agentValidation"

const RoutingAgentForm: React.FC<AgentFormProps> = props => {
    const { t } = useTranslation()
    const { agent, tabEntries, submitCallback, validateTabs, ownedByThisScenario } = props

    const project = useSelector(selectCurrentProject)
    const declarations = useSelector(selectAgentDeclarations)
    const dispatch = useDispatch<Dispatch>()

    const mainRef = React.useRef<TabPaneElement>(null)
    const advancedRef = React.useRef<TabPaneElement>(null)

    const validateOperatorQueueTabs = useCallback(() => {
        validateTabs && validateTabs([mainRef])
    }, [validateTabs])

    const initialValues = useMemo(
        () =>
            declarations && declarations[AgentType.RoutingAgent]
                ? agent
                    ? getValuesFromRoutingAgent(agent, declarations[AgentType.RoutingAgent])
                    : getDefaultRoutingAgentValues(declarations[AgentType.RoutingAgent])
                : null,
        [agent, declarations]
    )

    const validationSchema = useMemo(
        () => getValidationSchema(AgentType.RoutingAgent, ownedByThisScenario),
        [ownedByThisScenario]
    )

    useEffect(() => {
        if (project && !declarations?.[AgentType.RoutingAgent]) {
            dispatch(getAgentDeclarations(project.id, AgentType.RoutingAgent))
        }
    }, [project, dispatch, declarations])

    if (!project) {
        return null
    }

    if (!initialValues) {
        return <AgentFormLoader count={3} />
    }

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values: RoutingAgentValues) => {
                const request = buildRoutingAgentRequest(project.id, values)
                if (agent) {
                    dispatch(updateAgent(request, submitCallback))
                } else {
                    dispatch(createAgent(request, submitCallback))
                }
            }}
        >
            {formikProps => (
                <>
                    <OnSubmitValidationError formikProps={formikProps} onCallback={validateOperatorQueueTabs} />
                    <Tab.Pane eventKey={tabEntries[0].key} ref={mainRef}>
                        <FormikRoutingAgentFormGeneral {...props} {...formikProps} t={t} />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tabEntries[1].key} ref={advancedRef}>
                        <FormikRoutingAgentFormAdvanced {...props} {...formikProps} t={t} />
                    </Tab.Pane>
                </>
            )}
        </Formik>
    )
}

export default RoutingAgentForm
